import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Search, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  order_placed: "bg-blue-500/20 text-blue-400",
  proof_sent: "bg-violet-500/20 text-violet-400",
  proof_approved: "bg-cyan-500/20 text-cyan-400",
  in_production: "bg-amber-500/20 text-amber-400",
  quality_check: "bg-emerald-500/20 text-emerald-400",
  ready_to_ship: "bg-teal-500/20 text-teal-400",
  shipped: "bg-indigo-500/20 text-indigo-400",
  on_hold: "bg-red-500/20 text-red-400",
  blocked: "bg-red-700/20 text-red-400",
};

const PAYMENT_COLORS = {
  paid: "bg-green-500/20 text-green-400",
  partial: "bg-yellow-500/20 text-yellow-400",
  unpaid: "bg-red-500/20 text-red-400",
};

export default function OrderList({ onOpenOrder }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchOrders = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/oms/orders`, { withCredentials: true, params });
      setOrders(data);
    } catch (err) {
      console.error("Failed:", err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchOrders, 300);
    return () => clearTimeout(t);
  }, [fetchOrders]);

  const getDueIndicator = (dueDate, status) => {
    if (!dueDate || status === "shipped") return null;
    const due = new Date(dueDate);
    const now = new Date();
    const diff = (due - now) / (1000 * 60 * 60 * 24);
    if (diff < 0) return <span className="text-xs text-red-400 font-semibold">OVERDUE</span>;
    if (diff < 2) return <span className="text-xs text-yellow-400 font-semibold">DUE SOON</span>;
    return null;
  };

  return (
    <div data-testid="order-list">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-2xl font-bold text-white">Orders</h2>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              data-testid="order-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 text-zinc-300 rounded-lg px-3 py-2 text-sm"
            data-testid="status-filter"
          >
            <option value="">All Statuses</option>
            {Object.keys(STATUS_COLORS).map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-center py-12">Loading...</div>
      ) : orders.length === 0 ? (
        <div className="text-zinc-500 text-center py-12">No orders found</div>
      ) : (
        <div className="space-y-2">
          {orders.map((order) => (
            <div
              key={order.id}
              onClick={() => onOpenOrder(order.id)}
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 cursor-pointer hover:border-zinc-600 transition flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid={`order-row-${order.order_number}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-bold text-orange-400">{order.order_number}</span>
                  <Badge className={`text-[10px] ${STATUS_COLORS[order.status]}`}>
                    {order.status?.replace(/_/g, " ")}
                  </Badge>
                  <Badge className={`text-[10px] ${PAYMENT_COLORS[order.payment_status]}`}>
                    {order.payment_status}
                  </Badge>
                  {getDueIndicator(order.due_date, order.status)}
                </div>
                <p className="text-sm text-white font-medium">{order.customer_name}</p>
                <p className="text-xs text-zinc-500 truncate">{order.items?.map((i) => `${i.name} x${i.quantity}`).join(", ")}</p>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-white font-semibold">${order.total_amount?.toLocaleString()}</span>
                <span className="text-zinc-500 text-xs">{new Date(order.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
